import { inject, injectable } from 'inversify';
import type { ICineloopControlController } from './interfaces';
import type { ViewsService } from '@/engine/app/Views.service';
import type { CasePhaseData } from '@/engine/app/CasePhaseData/CasePhaseData.ts';
import type { AppController } from '@/engine/app/App.controller';
import type { ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command';
import { ImagesMode } from '@/engine/app/modules/images/interfaces.ts';
import { ImagesService } from '@/engine/app/modules/images/Images.service.ts';

import { TYPES } from '@/engine/ioc/types';
import { CORE_TYPES } from '@/engine/app/modules/core';
import { TYPES as ImagesTypes } from '@/engine/app/modules/images/iocTypes.ts';
import { IDisposable } from '@/engine/Utilities/IDisposable';
import { AutoContext } from '@/engine/Utilities/AutoContext';
import { ViewController } from '@/engine/app/View.controller';

@injectable()
export class CineloopControlController implements ICineloopControlController {
  private readonly _appController: AppController;

  private readonly _viewsService: ViewsService;

  private readonly _viewController: ViewController;

  private readonly _imagesService: ImagesService;

  private readonly _casePhaseData: CasePhaseData;

  private readonly _commandProcessor: ICommandProcessor;

  private _currentPhaseIndex: number | undefined;

  private _cineloopContext?: IDisposable;

  public constructor(
    @inject(TYPES.AppController) appController: AppController,
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.ViewController) viewController: ViewController,
    @inject(ImagesTypes.ImagesService) imagesService: ImagesService,
    @inject(TYPES.CasePhaseData) casePhaseData: CasePhaseData,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: ICommandProcessor
  ) {
    this._appController = appController;
    this._casePhaseData = casePhaseData;
    this._commandProcessor = commandProcessor;
    this._viewsService = viewsService;
    this._viewController = viewController;
    this._imagesService = imagesService;
  }

  public async playCineloop(shouldStartPlay: boolean): Promise<void> {
    this._cineloopContext?.dispose();
    this._cineloopContext = undefined;

    if (shouldStartPlay) {
      this._cineloopContext = new AutoContext(
        () => {
          const oldImagesMode = this._imagesService.imagesMode;
          if (oldImagesMode !== ImagesMode.MPR) {
            const oldClDescriptor = this._viewsService.currentView?.getActiveCenterLineDescriptor();
            // TODO: MPR images mode should always deselect centerline!
            //   fix code duplications with:
            //     useImageModeHandlers.ts
            //     OrientationPresets.controller.ts
            //     cinelooControlController.ts
            //     viewModelSelectionReactionStrategiesProvider.ts
            this._viewController.setActiveCenterLineDescriptor(undefined);
            this._imagesService.setImagesMode(ImagesMode.MPR);
            return {
              imagesMode: oldImagesMode,
              clDescriptor: oldClDescriptor
            };
          } else {
            return undefined;
          }
        },
        (memento?: { imagesMode: ImagesMode; clDescriptor?: string }) => {
          if (memento) {
            // TODO: POLYLINE images mode should always select centerline!
            //   fix code duplications with:
            //     useImageModeHandlers.ts
            //     OrientationPresets.controller.ts
            //     cinelooControlController.ts
            //     viewModelSelectionReactionStrategiesProvider.ts
            this._viewController.setActiveCenterLineDescriptor(memento.clDescriptor);
            this._imagesService.setImagesMode(memento?.imagesMode);
          }
        }
      );
    }

    this._setCinelooping(this._appController.isCineloopActive);
    await this._appController.triggerCineloop(this._viewsService.currentViewOrThrow.getImageStackDescriptor());
  }

  private _setCinelooping(shouldStartPlay: boolean): void {
    const imageStackDescriptor = this._viewsService.currentViewOrThrow.getImageStackDescriptor();
    if (shouldStartPlay) {
      this._currentPhaseIndex = this._casePhaseData.getActivePhaseIdx(imageStackDescriptor);
    } else {
      const undoPhaseIndex = this._currentPhaseIndex;
      const redoPhaseIndex = this._casePhaseData.getActivePhaseIdx(imageStackDescriptor);
      this._commandProcessor.addAnonymousCommand(
        async (): Promise<undefined> => {
          await this._appController.changePhaseAsync(imageStackDescriptor, redoPhaseIndex);
        },
        async (): Promise<undefined> => {
          await this._appController.changePhaseAsync(imageStackDescriptor, undoPhaseIndex);
        }
      );
    }
  }
}
